import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import Layout from "@/components/Layout";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  MessageSquare,
  Headphones,
  FileText,
  CheckCircle
} from "lucide-react";
import { useNavigate } from 'react-router-dom';
import MessageDialog from '../components/MessageDialog';

const Contact = () => {
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const navigate = useNavigate();
  const methodClick = (action: string) => {
    switch(action) {
      case "立即拨打":
        break;
      case "发送邮件":
        break;
      case "开始对话":
        // 这里可以添加开始对话的逻辑
        break;
      // case "访问社区":
      //   navigate('/community');
      //   break;
      case "开始编辑":
        setIsOpen(true);
        break;
    }
  };

  const contactMethods = [
    {
      icon: <Phone className="w-6 h-6" />,
      title: "电话咨询",
      description: "工作日 8:30-17:30",
      contact: "0571-58837995  & 19706826782",
      action: "立即拨打"
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: "邮件联系",
      description: "24小时内回复",
      contact: "contact@hzgzyrj.cn",
      action: "发送邮件"
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: "发送消息",
      description: "实时消息发送",
      contact: "点击开始编辑",
      action: "开始编辑"
    },
    // {
    //   icon: <Users className="w-6 h-6" />,
    //   title: "技术社区",
    //   description: "与专家交流",
    //   contact: "加入社区讨论",
    //   action: "访问社区"
    // },
  ];

  const officeInfo = [
    {
      title: "总部地址",
      address: "浙江省杭州市富阳区大源镇塘子畈街200号",
      details: "杭州光学精密机械研究所内"
    },
    // {
    //   title: "研发中心",
    //   address: "北京市海淀区",
    //   details: "中国科学院软件研究所"
    // }
  ];

  const supportTypes = [
    {
      icon: <Headphones className="w-5 h-5" />,
      title: "技术支持",
      description: "产品使用、技术问题咨询"
    },
    {
      icon: <FileText className="w-5 h-5" />,
      title: "商务合作",
      description: "产品采购、合作洽谈"
    },
    {
      icon: <MessageSquare className="w-5 h-5" />,
      title: "意见反馈",
      description: "产品建议、功能需求"
    }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              联系我们
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              我们的专业团队随时为您提供技术支持、产品咨询和解决方案服务
            </p>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {contactMethods.map((method, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 text-blue-600">
                    {method.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {method.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    {method.description}
                  </p>
                  <p className="text-blue-600 font-medium mb-4">
                    {method.contact}
                  </p>
                  <Button variant="outline" size="sm" className="w-full" onClick={() => methodClick(method.action)}>
                    {method.action}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-6">
              {/* Business Hours */}
              <Card className="h-full">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Clock className="w-5 h-5" />
                    <span>服务时间</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">工作日</span>
                      <span className="font-medium">8:30 - 17:30</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">周末</span>
                      <span className="font-medium">10:00 - 17:00</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">在线客服</span>
                      <span className="font-medium">24/7</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">邮件支持</span>
                      <span className="font-medium">24小时内回复</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <div className="space-y-6">
              {/* Office Locations */}
              <Card className="h-full">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <MapPin className="w-5 h-5" />
                    <span>办公地址</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {officeInfo.map((office, index) => (
                    <div key={index} className="border-l-4 border-blue-500 pl-4">
                      <h4 className="font-semibold text-gray-900 mb-1">
                        {office.title}
                      </h4>
                      <p className="text-gray-600 mb-1">{office.address}</p>
                      <p className="text-sm text-gray-500">{office.details}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>
          
          <div className="space-y-6">
            {/* Support Types */}
            <Card>
              <CardHeader>
                <CardTitle>我们能为您提供</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {supportTypes.map((type, index) => (
                    <div key={index} className="flex items-start space-x-3 p-3 border rounded-lg">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 flex-shrink-0">
                        {type.icon}
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{type.title}</h4>
                        <p className="text-sm text-gray-600">{type.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Map Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              找到我们
            </h2>
            <p className="text-xl text-gray-600">
              欢迎到访我们的办公室，与我们的团队面对面交流
            </p>
          </div>
          
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <a
              href="https://surl.amap.com/puHsPM199Bk"
              target="_blank"
              rel="noopener noreferrer"
              className="block cursor-pointer hover:opacity-95 transition-opacity"
            >
              <img
                src="/images/map-preview.png"
                alt="杭州光学精密机械研究所地图"
                className="w-full h-auto"
              />
            </a>
          </div>
        </div>
      </section>

      {/* Message Dialog */}
      <MessageDialog
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      />
    </Layout>
  );
};

export default Contact;